import { DetailBar, Group, Icon, Number, Tag, Text, Title } from "components/common"
import { t } from "i18next"
import { useMemo, useState } from "react"
import { localisedValue } from "utils"
import cx from "clsx"
import { Radio, Row } from "antd"
import theme from "utils/themeVars"
import { dateTimeFunction } from "utils/date"
import { Link } from "react-router-dom"
import { DATE_FORMAT } from "constants"
import { TIME_FORMAT } from "constants"
import FractionalSaleDetailCard from "./fractionalSaleDetailCard"
import { configs } from "configs"

const stepsIcon = <Icon size="8px" icon="ImCheckmark" stroke={0.5} />

function getDefaultSalesTab({ vasSales = [], transactionalSales = [], fractionalSales = [] }) {
  const hasV = vasSales.length > 0
  const hasT = transactionalSales.length > 0
  const hasF = fractionalSales.length > 0
  if (!hasV && !hasT && !hasF) return null
  if (hasV && hasT) return "vas"
  if (hasV) return "vas"
  if (hasT) return "transactional"
  return "fractional"
}

const getActiveSales = (e, effectiveType) => {
  return [
    { label: t("Unit"), data: e.contract?.unitNumber },
    { label: t("Unit Price"), data: <Number isStringOnly={true} isCurrency={true} value={e.contract.unitPrice} /> },
    {
      label: t("Unit Discount"),
      data: (
        <>
          {" "}
          <Number isCurrency={true} isStringOnly={true} value={e.contract.dealDiscount} /> (
          <Number
            isStringOnly={true}
            value={(e.contract.dealDiscount / e.contract.unitPrice) * 100}
            digitAfterFraction={(e.contract.dealDiscount / e.contract.unitPrice) * 100 < 0.01 ? 3 : 2}
          />
          )%
        </>
      ),
    },
    {
      label: effectiveType === "vas" ? t("BSF Fee") : t("Deal Price"),
      data: <Number isStringOnly={true} isCurrency={true} value={e.contract.dealPrice} />,
    },
    {
      label: t("Paid Amount"),
      data: <Number isStringOnly={true} isCurrency={true} value={e.contract.paymentProgress.amountReceived} />,
    },
  ]
}

const getDetailData = (e, effectiveType) => {
  const scopeParam =
    effectiveType === "vas"
      ? `&status=all&contractScope=vas`
      : effectiveType === "fractional"
        ? `&status=all&contractScope=fractional`
        : "&status=all&contractScope=transactional"

  return [
    {
      label: (
        <Row justify="space-between" align="middle">
          <Text>{t("Contract ID")}</Text>
          <Tag type="light" color={theme["accentColor"]}>
            <span style={{ color: "#1d9ec2", fontWeight: "bold" }}>
              {localisedValue({ object: e?.contract, key: "contractType" })}
            </span>
          </Tag>
        </Row>
      ),
      value: (
        <Row align="bottom" style={{ gap: "2px 4px" }}>
          <Text>
            {e?.contract?.contractId ? (
              <Link to={`/contracts/?contractId=${e?.contract?.contractId}${scopeParam}`}>
                {e?.contract?.contractId}
              </Link>
            ) : (
              e?.contract?.contractId
            )}
          </Text>{" "}
          {e.contract.expiryDate && (
            <Text className="small" type="danger">
              ({t("Expiry")} {dateTimeFunction({ date: e.contract.expiryDate, format: DATE_FORMAT })}{" "}
              {dateTimeFunction({ date: e.contract.expiryDate, format: TIME_FORMAT })})
            </Text>
          )}
        </Row>
      ),
    },
    {
      label: t("Due Amount"),
      value: (
        <Number
          isStringOnly={true}
          isCurrency={true}
          value={e.contract.paymentProgress.remainingCompleteDownPaymentAmount}
        />
      ),
    },
  ]
}

const getStepsData = (e, effectiveType) => {
  const contractStagesObject = e?.contract?.contractStages || {}
  const showRega = e?.contract?.project?.needsRega
  const showSPA = e?.contract?.project?.needsSpa
  const showBookingForm = e?.contract?.project?.needsBookingForm
  const steps = [
    {
      title: effectiveType === "vas" ? t("BSF") : t("Token"),
      icon: stepsIcon,
      key: "token",
    },
    { title: t("PDP"), icon: stepsIcon, key: "partial_down_payment" },
    ...(!!showBookingForm
      ? [
          {
            title: configs.showBuyerFeeCheckbox ? t("URF") : t("BF"),
            icon: stepsIcon,
            status:
              e.inquiry?.Documents?.["booking-form"]?.disposition?.key === "signed" ||
              e.inquiry?.Documents?.["booking-form"]?.disposition?.key === "dispatched"
                ? "finish"
                : e.current >= 2
                  ? "wait pending"
                  : "wait",
          },
        ]
      : []),
    ...(!!showRega
      ? [
          {
            title: t("REGA"),
            icon: stepsIcon,
            key: "rega_contract",
            status:
              e.inquiry?.Documents?.["rega"]?.disposition?.key === "signed"
                ? "finish"
                : e.current >= 2
                  ? "wait pending"
                  : "wait",
          },
        ]
      : []),
    ...(!!showSPA
      ? [
          {
            title: t("SPA"),
            icon: stepsIcon,
            key: "spa",
            status:
              e.inquiry?.Documents?.["spa"]?.disposition?.key === "signed"
                ? "finish"
                : e.current >= 2
                  ? "wait pending"
                  : "wait",
          },
        ]
      : []),
    { title: t("CW"), icon: stepsIcon, key: "complete_down_payment" },
    { title: t("Verified"), icon: stepsIcon, key: "sold" },
  ]
  return steps.map(step => ({
    ...step,
    ...(contractStagesObject[step.key] && { stageUpdatedAt: contractStagesObject[step.key] }),
  }))
}

const ClientSalesDetail = ({ data, styles }) => {
  const { vasSales = [], transactionalSales = [], fractionalSales = [] } = data?.activeSales || {}
  const [selectedSales, setSelectedSales] = useState(null)

  const effectiveType = useMemo(() => {
    const has = {
      vas: vasSales.length > 0,
      transactional: transactionalSales.length > 0,
      fractional: fractionalSales.length > 0,
    }
    const chosen = selectedSales ?? getDefaultSalesTab({ vasSales, transactionalSales, fractionalSales })
    if (chosen && has[chosen]) return chosen
    return getDefaultSalesTab({ vasSales, transactionalSales, fractionalSales })
  }, [selectedSales, vasSales, transactionalSales, fractionalSales])

  const salesData = useMemo(() => {
    if (effectiveType === "vas") return vasSales
    if (effectiveType === "transactional") return transactionalSales
    if (effectiveType === "fractional") return fractionalSales
    return []
  }, [effectiveType, vasSales, transactionalSales, fractionalSales])

  const salesOptions = useMemo(() => {
    const opts = []
    const style = { flex: 1, textAlign: "center" }
    if (vasSales?.length > 0) {
      opts.push({
        label: t("BSF"),
        value: "vas",
        key: "vas",
        style,
      })
    }
    if (transactionalSales?.length > 0) {
      opts.push({
        label: t("Transactional"),
        value: "transactional",
        key: "transactional",
        style,
      })
    }
    if (fractionalSales?.length > 0) {
      opts.push({
        label: t("Fractional"),
        value: "fractional",
        key: "fractional",
        style,
      })
    }
    return opts
  }, [vasSales, transactionalSales, fractionalSales])

  const showSalesTabs = salesOptions.length > 1
  const hasAnyActiveSales =
    (vasSales?.length ?? 0) > 0 || (transactionalSales?.length ?? 0) > 0 || (fractionalSales?.length ?? 0) > 0

  return (
    <>
      {hasAnyActiveSales && (
        <div>
          <Title level={3}>{t("Active Sales")}</Title>
          {showSalesTabs && (
            <Radio.Group
              block
              size="large"
              value={effectiveType}
              onChange={e => setSelectedSales(e.target.value)}
              style={{ display: "flex", width: "100%", marginBottom: 16 }}
              optionType="button"
              options={salesOptions}
            />
          )}
          <Group gap="24px">
            {salesData?.map((e, index) => {
              if (effectiveType === "fractional") {
                return <FractionalSaleDetailCard key={e.inquiry?.id ?? e.contract?.contractId ?? index} sale={e} />
              }
              if (e?.inquiry?.interestedIn === "primary") {
                return (
                  <DetailBar
                    key={e.inquiry.id}
                    collapsible
                    detailData={getDetailData(e, effectiveType)}
                    listData={getActiveSales(e, effectiveType)}
                    stepsProps={{
                      data: getStepsData(e, effectiveType),
                      current: e.current,
                      className: cx(styles.steps, effectiveType === "vas" ? styles.stepSecondary : ""),
                    }}
                    {...(effectiveType === "vas" && {
                      bgColor: "#EFF8FC",
                      borderColor: theme["accent-color"],
                      className: styles.collapseSecondary,
                    })}
                    detailHeader={
                      e.contract.project?.name && (
                        <div>
                          <Text type="info" style={{ fontWeight: 600 }} block>
                            {e?.contract?.project?.url && e?.contract?.project?.name ? (
                              <a href={e?.contract?.project?.url} target="_blank" rel="noreferrer">
                                {localisedValue({ object: e?.contract?.project, key: "name" })}
                              </a>
                            ) : (
                              localisedValue({ object: e?.contract?.project, key: "name" })
                            )}
                          </Text>
                          <Text type="secondaryLight">{localisedValue({ object: e.contract.Type, key: "name" })}</Text>
                        </div>
                      )
                    }
                  />
                )
              }
              return null
            })}
          </Group>
        </div>
      )}
    </>
  )
}

export default ClientSalesDetail
